import {
  ClientContextFilesMetadata,
  ClientContextScope,
  IClientCreatePayload,
  IContextFileEntry,
  ICreateClientRequest,
} from '../Types/clientCreate';
import { SubscriptionTier } from '../Types/limits';
import { validateFile, validateFileSize } from './FileValidation';
import { IFileValidationRules } from '../Types/assets';

export const MAX_CLIENT_CONTEXT_FILES = 20;
export const MAX_CLIENT_CONTEXT_FILE_SIZE = 100 * 1024 * 1024; // 100 MiB

export const INDIVIDUAL_CONTEXT_SCOPES: ClientContextScope[] = [
  'background',
  'messaging',
  'industry',
];

const WIRE_SCOPE_ORDER: ClientContextScope[] = ['background', 'messaging', 'industry', 'all'];

/** Rules aligned with backend client context file validation. */
export const CLIENT_CONTEXT_FILE_RULES: IFileValidationRules = {
  maxSize: MAX_CLIENT_CONTEXT_FILE_SIZE,
  allowedTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'text/plain',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
  ],
  allowedExtensions: [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.txt',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.mp4',
    '.mov',
    '.avi',
  ],
};

export function ensureDomainProtocol(domain: string): string {
  const trimmedDomain = domain.trim();
  if (!trimmedDomain) return trimmedDomain;

  if (trimmedDomain.startsWith('http://') || trimmedDomain.startsWith('https://')) {
    return trimmedDomain;
  }

  return `https://${trimmedDomain}`;
}

export function hasAllIndividualScopes(scopes: ClientContextScope[]): boolean {
  return INDIVIDUAL_CONTEXT_SCOPES.every((scope) => scopes.includes(scope));
}

/** Normalize UI scope selection to wire `file_metadata.scopes`. */
export function toWireScopes(scopes: ClientContextScope[]): ClientContextScope[] {
  if (scopes.includes('all') || hasAllIndividualScopes(scopes)) {
    return ['all'];
  }

  return WIRE_SCOPE_ORDER.filter((scope) => scope !== 'all' && scopes.includes(scope));
}

export function fileRequiresSuggestIndustries(scopes: ClientContextScope[]): boolean {
  return scopes.includes('industry') || toWireScopes(scopes).includes('all');
}

export function hasBackgroundSource(
  backgroundText: string,
  files: Pick<IContextFileEntry, 'scopes'>[],
): boolean {
  if (backgroundText.trim().length > 0) return true;
  return files.some((f) => {
    const wire = toWireScopes(f.scopes);
    return wire.includes('background') || wire.includes('all');
  });
}

export function buildFileMetadata(
  files: Pick<IContextFileEntry, 'scopes'>[],
): ClientContextFilesMetadata {
  return files.map((entry, index) => ({
    index,
    scopes: toWireScopes(entry.scopes),
  }));
}

export function buildClientCreatePayload(request: ICreateClientRequest): IClientCreatePayload {
  const payload: IClientCreatePayload = {
    name: request.name.trim(),
    domain: ensureDomainProtocol(request.domain),
    suggest_industries: request.suggestIndustries === true,
    subscription_tier: request.subscriptionTier,
  };

  const backgroundText = request.backgroundText?.trim() ?? '';
  if (request.relationshipContext) payload.relationship_context = request.relationshipContext;
  if (backgroundText) {
    payload.background_text = backgroundText;
  }

  const industryGuidance = request.industrySuggestionGuidance?.trim() ?? '';
  if (payload.suggest_industries && industryGuidance) {
    payload.industry_suggestion_guidance = industryGuidance;
  }

  return payload;
}

export function validateContextFile(file: File): string | null {
  const sizeResult = validateFileSize(file, MAX_CLIENT_CONTEXT_FILE_SIZE);
  if (!sizeResult.isValid) {
    return sizeResult.errors[0] ?? 'File is not valid';
  }
  const result = validateFile(file, CLIENT_CONTEXT_FILE_RULES);
  if (!result.isValid) {
    return result.errors[0] ?? 'File type is not supported';
  }
  return null;
}

export function hasFilesWithoutScopes(files: IContextFileEntry[]): boolean {
  return files.some((f) => f.scopes.length === 0);
}

export function hasIndustryScopedFilesWithoutToggle(
  files: IContextFileEntry[],
  suggestIndustries: boolean,
): boolean {
  return !suggestIndustries && files.some((f) => fileRequiresSuggestIndustries(f.scopes));
}

export interface CreateClientFormErrors {
  relationship?: string;
  name?: string;
  domain?: string;
  subscriptionTier?: string;
  background?: string;
  industrySuggestion?: string;
  files?: string;
  submit?: string;
}

export function validateCreateClientForm(
  name: string,
  domain: string,
  backgroundText: string,
  files: IContextFileEntry[],
  subscriptionTier: SubscriptionTier | null = null,
  suggestIndustries = false,
  industrySuggestionGuidance = '',
): CreateClientFormErrors {
  const errors: CreateClientFormErrors = {};

  if (!name.trim()) {
    errors.name = 'Client name is required';
  }

  if (!domain.trim()) {
    errors.domain = 'Website or domain is required';
  }

  if (!subscriptionTier) {
    errors.subscriptionTier = 'Choose a subscription tier';
  }

  if (!hasBackgroundSource(backgroundText, files)) {
    errors.background =
      'Provide campaign background text and/or at least one file with Background or All selected. Messaging-only or industry-only files are not enough.';
  }

  if (hasFilesWithoutScopes(files)) {
    errors.files = 'Each uploaded file must have at least one purpose selected.';
  }

  if (industrySuggestionGuidance.trim().length > 0 && !suggestIndustries) {
    errors.industrySuggestion =
      'Turn on industry suggestions to use guidance text, or clear the guidance.';
  }

  if (hasIndustryScopedFilesWithoutToggle(files, suggestIndustries)) {
    errors.industrySuggestion =
      'Industry-scoped files require industry suggestions to be turned on.';
  }

  if (files.length > MAX_CLIENT_CONTEXT_FILES) {
    errors.files = `At most ${MAX_CLIENT_CONTEXT_FILES} files are allowed`;
  }

  return errors;
}

export function formatCreateClientApiError(error: unknown): string {
  const axiosError = error as {
    response?: { data?: { detail?: string | Array<{ msg?: string; loc?: string[] }> } };
    message?: string;
  };

  const detail = axiosError.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item.msg ?? JSON.stringify(item)).join('; ');
  }
  if (detail && typeof detail === 'object') {
    return JSON.stringify(detail);
  }

  return axiosError.message ?? 'Failed to create client. Please try again.';
}

export function scopeLabel(scope: ClientContextScope): string {
  switch (scope) {
    case 'background':
      return 'Background';
    case 'messaging':
      return 'Messaging';
    case 'industry':
      return 'Industry';
    case 'all':
      return 'All';
    default:
      return scope;
  }
}

export function formatScopesForDisplay(scopes: ClientContextScope[]): string {
  const wire = toWireScopes(scopes);
  if (wire.length === 0) {
    return '(none selected)';
  }
  if (wire.includes('all')) {
    return scopeLabel('all');
  }
  return wire.map((scope) => scopeLabel(scope)).join(', ');
}
